/**
 * The booking form for "Book appointment" and "Rebook" (docs/ux/clinic.md):
 * a Dialog on desktop, a Sheet on the phone. Clinic, date, time and whether
 * the patient has confirmed; it previews the move, shows a 409 inline and
 * never fires twice. VitalSign is the source of appointments; this keys the
 * one the matter waits on.
 */

import { EVENT_DISPLAY, eventLabel, type Status } from '@beam/core/status';
import { useQuery } from '@tanstack/react-query';
import { cn } from 'cn';
import { useId, useState, type FormEvent } from 'react';
import { api } from '@/app/api';
import type { TransitionOption } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { StatusChip } from '@/components/matter/StatusChip';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { localDateKey } from '@/lib/format';
import { CONFLICT_TEXT } from './useMatter';

export type Clinic = { id: string; name: string; suburb: string };
export type BookingValues = { clinicId: string; startsAt: string; confirmed: boolean };

export function useClinics() {
  return useQuery({ queryKey: ['registry', 'clinics'], queryFn: () => api<Clinic[]>('/api/registry/clinics'), staleTime: 300_000 });
}

export type BookingFormProps = {
  matterRef: string;
  from: Status;
  option: TransitionOption | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  conflict: boolean;
  offline: boolean;
  onSubmit: (values: BookingValues) => void;
};

export function BookingForm(props: BookingFormProps) {
  const phone = useIsPhone();
  const { open, onOpenChange, option } = props;
  if (!option) return null;
  const title = eventLabel(option.kind);
  const to = option.to ?? 5;
  const description = (
    <span className="flex flex-wrap items-center gap-1.5 text-sm text-foreground">
      <span className="tabular font-semibold">{props.matterRef}</span> moves from <StatusChip status={props.from} size="sm" /> to <span className="inline-flex items-center whitespace-nowrap"><StatusChip status={to} size="sm" />.</span> VitalSign is the source of appointments; this keys the one the case waits on.
    </span>
  );
  const body = <Body key={`${option.kind}-${String(open)}`} {...props} option={option} className={phone ? 'px-4' : undefined} />;
  if (phone) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[92svh] overflow-y-auto rounded-t-[20px] shadow-beam-3">
          <div aria-hidden="true" className="mx-auto mt-2 h-1 w-10 rounded-full bg-beam-line-strong" />
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

function defaultDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return localDateKey(d);
}

function Body({ option, busy, conflict, offline, onSubmit, onOpenChange, className }: BookingFormProps & { option: TransitionOption; className?: string }) {
  const phone = useIsPhone();
  const clinics = useClinics();
  const id = useId();
  const [clinicId, setClinicId] = useState('');
  const [date, setDate] = useState(defaultDate());
  const [time, setTime] = useState('10:30');
  const [confirmed, setConfirmed] = useState(true);
  const [errors, setErrors] = useState<{ clinic?: string; when?: string }>({});
  const startsAt = date && time ? new Date(`${date}T${time}:00`) : null;
  const past = startsAt !== null && startsAt.getTime() <= Date.now();

  function submit(e?: FormEvent) {
    e?.preventDefault();
    const next: typeof errors = {};
    if (!clinicId) next.clinic = 'Choose the clinic';
    if (!startsAt || Number.isNaN(startsAt.getTime())) next.when = 'Choose the date and time';
    else if (past) next.when = 'The appointment must be in the future';
    setErrors(next);
    if (next.clinic || next.when || !startsAt) return;
    onSubmit({ clinicId, startsAt: startsAt.toISOString(), confirmed });
  }

  const confirmLabel = busy ? EVENT_DISPLAY[option.kind].busy : eventLabel(option.kind);
  const footer = (
    <>
      <Button type="button" variant="outline" size={phone ? 'lg' : 'default'} className={phone ? 'h-11' : undefined} onClick={() => onOpenChange(false)} disabled={busy}>Keep as is</Button>
      <Button type="submit" data-primary="true" size={phone ? 'lg' : 'default'} className={phone ? 'h-11' : undefined} disabled={busy || offline || !option.allowed} aria-busy={busy}>{confirmLabel}</Button>
    </>
  );

  return (
    <form onSubmit={submit} className={cn('flex flex-col gap-4', className)} noValidate data-slot="booking-form">
      {conflict ? <Alert role="alert" variant="destructive"><AlertTitle>{CONFLICT_TEXT}</AlertTitle></Alert> : null}
      {!option.allowed ? <Alert role="alert"><AlertTitle>This move is no longer allowed: {option.reason}</AlertTitle></Alert> : null}
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${id}-clinic`}>Clinic</Label>
        <Select value={clinicId} onValueChange={setClinicId}>
          <SelectTrigger id={`${id}-clinic`} aria-label="Clinic" aria-invalid={errors.clinic ? true : undefined} className={phone ? 'h-11 text-base' : 'h-9'}><SelectValue placeholder={clinics.isPending ? 'Loading clinics' : 'Choose the clinic'} /></SelectTrigger>
          <SelectContent>
            {(clinics.data ?? []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}, {c.suburb}</SelectItem>)}
          </SelectContent>
        </Select>
        {errors.clinic ? <p className="text-[13px] text-beam-error">{errors.clinic}</p> : null}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor={`${id}-date`}>Date</Label>
          <Input id={`${id}-date`} type="date" value={date} min={localDateKey()} onChange={(e) => setDate(e.target.value)} aria-invalid={errors.when ? true : undefined} className={phone ? 'h-11 text-base' : 'h-9'} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={`${id}-time`}>Time</Label>
          <Input id={`${id}-time`} type="time" value={time} step={300} onChange={(e) => setTime(e.target.value)} aria-invalid={errors.when ? true : undefined} className={phone ? 'h-11 text-base' : 'h-9'} />
        </div>
      </div>
      {errors.when ? <p className="text-[13px] text-beam-error">{errors.when}</p> : null}
      <div className="flex min-h-11 items-center justify-between gap-3">
        <Label htmlFor={`${id}-confirmed`}>Confirmed with the patient</Label>
        <Switch id={`${id}-confirmed`} checked={confirmed} onCheckedChange={setConfirmed} />
      </div>
      {phone ? <SheetFooter className="flex-row justify-end gap-2 px-0 pt-2">{footer}</SheetFooter> : <DialogFooter className="gap-2">{footer}</DialogFooter>}
    </form>
  );
}
